<template>
  <section>
    <div class="container section-bg" :style="sectionBgImage">
      <div class="row px-space-xxs px-md-space-lg py-space-lg">
        <div class="col-12 col-lg-4">
          <div class="bg-white py-space-sm py-md-space-lg px-space-sm px-md-space-md">
            <h2 class="h2-medium mb-space-sm">{{ data.title }}</h2>
            <div v-html="data.text"></div>
          </div>
        </div>
        <div class="col-12 col-lg-8 px-space-xs px-md-space-md py-space-lg">
          <div class="menu-buttons-wrap pb-space-xxs">
            <div class="menu-buttons text-white d-flex justify-content-start position-relative">
              <h3
                v-for="(tab, tabIndex) in data.tabs"
                :key="tabIndex"
                :ref="(el) => (tabRefs[tab.id] = el as HTMLElement)"
                class="h3-large me-space-md pointer"
                :class="{ 'text-secondary': selectedTab === tabIndex }"
              >
                <button
                  type="button"
                  class="bg-transparent border-0 p-0 text-reset fw-bold text-start"
                  :aria-current="selectedTab === tabIndex ? 'true' : undefined"
                  @click="handleTabClick(tab.id, tabIndex)"
                >
                  {{ tab.title }}
                </button>
              </h3>

              <div
                ref="yellowbar"
                :style="{
                  width: yellowbarWidth + 'px',
                  left: yellowbarOffset + 'px'
                }"
                class="yellow-bar bg-secondary position-absolute"
              ></div>
            </div>
          </div>
          <div
            ref="tabdecription"
            class="tab-text-hide text-white mt-space-md mb-space-md"
            v-html="tabsText"
          ></div>
          <img
            :alt="data.tabs[selectedTab].title"
            class="laptop-img"
            :src="data.tabs[selectedTab].image"
            loading="lazy"
            width="999"
            height="728"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

export interface Tabs {
  id: string;
  image: string;
  text: string;
  title: string;
}
export interface Data {
  title: string;
  text: string;
  bgImage: string;
  tabs: Array<Tabs>;
}
interface Props {
  data: Data;
}

const emits = defineEmits<{
  /**
   *  Emitted when the Tab is clicked.
   */
  (e: "tabClicked", { tabIndex }: { tabIndex: number }): void;
}>();

const props = withDefaults(defineProps<Props>(), {});

const selectedTab = ref(0);
const yellowbarWidth = ref(0);
const yellowbarOffset = ref(0);
const tabsText = ref<string | null>("");
const tabRefs = ref<{ [key: string]: HTMLElement | null }>({});
const tabdecription = ref<{ [key: string]: HTMLElement | null }>({});

const sectionBgImage = computed(() => {
  return { "background-image": `url('${props.data.bgImage}')` };
});

onMounted(() => {
  updateSelectedTab(props.data.tabs[0].id, 0);
});

const handleTabClick = (id: string, tabIndex: number) => {
  updateSelectedTab(id, tabIndex);
  emits("tabClicked", { tabIndex: tabIndex });
};

const updateSelectedTab = (item: string, tabIndex: number) => {
  selectedTab.value = tabIndex;
  const currentTabRef = tabRefs.value?.[item];
  if (currentTabRef) {
    yellowbarWidth.value = currentTabRef.clientWidth;
    yellowbarOffset.value = currentTabRef.offsetLeft;
  }
  const tabTextRef = tabdecription?.value;
  if (tabTextRef instanceof HTMLElement && tabTextRef.classList.contains("tab-text-show")) {
    tabTextRef.classList.remove("tab-text-show");
  }
  if (tabTextRef instanceof HTMLElement && tabTextRef.classList.contains("tab-text-hide")) {
    tabTextRef.classList.remove("tab-text-hide");
  }

  // Disappear current text
  if (tabTextRef instanceof HTMLElement) {
    tabTextRef.classList.add("tab-text-hide");
  }

  // Set text
  setTimeout(() => (tabsText.value = props.data.tabs[selectedTab.value].text), 80);

  // Appear text
  setTimeout(() => {
    if (tabTextRef instanceof HTMLElement) {
      tabTextRef.classList.add("tab-text-show");
    }
  }, 80);
};
</script>

<style lang="scss" scoped>
.section-bg {
  background-repeat: no-repeat;
  background-size: cover;
}
.menu-buttons-wrap {
  border-bottom: 1px solid #fff;
}
.menu-buttons {
  padding-bottom: 4px;
}
.pointer {
  cursor: pointer;
}
.yellow-bar {
  width: 100%;
  height: 8px;
  position: absolute;
  left: 0;
  bottom: -0.5rem;
  transition: 0.3s all;
}
.tab-text-hide {
  opacity: 0;
  transition: 0.1s all;
}
.tab-text-show {
  opacity: 1;
  transition: 0.1s all;
}
.laptop-img {
  width: auto;
  height: 410px;
}

@media (max-width: 600px) {
  .laptop-img {
    height: 236px;
  }
}
</style>
